/* eslint-disable complexity */
/* eslint-disable no-negated-condition */
import {flowResult} from 'mobx';
import {JobStore} from '@/stores';
import {messageManager} from '@/utils/errorHandler/MessageManager';
import {baseHandleError} from '@/utils/errorHandler/baseHandleError';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {shouldRecoverBeforeEnterJob, shouldRecoverTerminated} from '@/utils/job/autoTermination';

/**
 * 处理任务选择逻辑（列表点击 / URL 恢复）
 * @param store - 全局 store（包含 projectsListStore、taskRunningStore）
 * @param selectJob - 当前选择的任务
 */
export async function updateSelectJobUtil(
    store: any,
    selectJob: JobStore
) {
    const {jobsListStore} = store.projectsListStore;
    const jobId = selectJob.job_id || jobIdSyncService.getJobIdFromUrl() || '';

    // 幂等：如果当前已选中的就是该 job，则不重复处理
    if (jobsListStore.selectedJobId === jobId) {
        return;
    }

    try {
        // 1.更新选中的任务 ID
        jobsListStore.setSelectedJobId(jobId);
        // 2. 先设置基本信息（UI 立即响应）
        jobsListStore.setSelectedJobDetail(selectJob);

    } catch (error) {
        baseHandleError(error, {
            messageApi: messageManager.message as any,
            customMessage: `任务详情获取失败！${error.message}`
        });
    }
}

/**
 * @description 根据 jobCandidate 初始化 jobDetail 和 taskRunningStore
 * 可用于刷新恢复 / 点击任务进入 / 任务状态切换等场景
 */
export async function defaultSelectJobUtil(
    store: any,
    jobCandidate: JobStore | undefined,
    setLoading?: (loading: boolean) => void
) {
    if (!jobCandidate || jobCandidate.isDeleted) {
        setLoading?.(false);
        return;
    }

    const {projectsListStore, taskRunningStore} = store;
    const {jobsListStore} = projectsListStore;

    try {
        setLoading?.(true);

        const newJob = await flowResult(jobCandidate.fetchJobDetail());
        await jobsListStore.setSelectedJobId(newJob.job_id);
        await jobsListStore.setSelectedJobDetail(newJob);
        // 修改：草稿任务不启动轮询，其他任务都需要获取进度并启动轮询
        if (newJob.isDRAFT) {
            return;
        }

        // 非草稿任务：获取进度并启动轮询
        taskRunningStore.operationLoadingStore.startOp('fetchProgress');

        // 普通恢复任务进入页面前恢复；自动终止任务等弹窗确认后再恢复
        if (shouldRecoverBeforeEnterJob(newJob) || shouldRecoverTerminated(newJob)) {
            await flowResult(jobsListStore.selectedJobDetail.fetchPostRecovery(newJob.job_id));
        }

        await flowResult(taskRunningStore.fetchProgress(newJob.job_id));

        // 关键修改：根据任务状态决定是否启动轮询
        if (taskRunningStore.isTerminatedOrCompletedFiled) {
            taskRunningStore.stopPolling();
        } else {
            if (taskRunningStore.isPolling) {
                await taskRunningStore.stopPolling();
            }
            if (!taskRunningStore.isTerminatedOrCompletedFiled && !newJob.isDRAFT) {
                taskRunningStore.startPolling();
            }
        }
    }
    catch (error) {
        baseHandleError(error, {
            messageApi: messageManager.notification as any,
            customMessage: `任务详情获取失败！${error.message}`
        });
    }
    finally {
        setLoading?.(false);
        taskRunningStore.operationLoadingStore.endOp('fetchProgress');
    }
}
